import { RootState } from "@/store/store";
import { clearWarning, setWarning } from "@/store/warningSlice";
import { navigateToSubscribtion } from "@/utils/helpers";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CgClose } from "react-icons/cg";
import { RiErrorWarningFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export default function SubscriptionWarning() {
  const navigate = useNavigate();
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const restaurantData = useSelector(
    (state: RootState) => state.resturant.resturantData,
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const warning = useSelector((state: RootState) => state.warning);
  const { renew, upgrade, noPlan } = warning;

  const expireTimestamp = restaurantData?.subscription?.expire || 0;

  function getDaysLeft(timestamp: number): number {
    const now = new Date();
    const expireDate = new Date(timestamp * 1000);
    const diffTime = expireDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  const daysLeft = getDaysLeft(expireTimestamp);
  const isCritical = daysLeft <= 5;

  const bgColor = isCritical
    ? "bg-red-100 border-red-400 text-red-900"
    : "bg-yellow-200 border-yellow-400 text-yellow-900";

  const hoverTextColor = isCritical
    ? "hover:text-red-700"
    : "hover:text-yellow-700";

  useEffect(() => {
    if (restaurantData?.subscription === null) {
      dispatch(setWarning({ noPlan: true }));
      dispatch(setWarning({ upgrade: false }));
      dispatch(setWarning({ renew: false }));
      return;
    }
    if (!restaurantData?.subscription) return;

    if (
      getDaysLeft(restaurantData.subscription.expire) <= 30 &&
      restaurantData.subscription.isFree === false
    ) {
      dispatch(setWarning({ renew: true }));
      dispatch(setWarning({ upgrade: false }));
    } else if (
      getDaysLeft(restaurantData.subscription.expire) <= 30 &&
      restaurantData.subscription.isFree === true
    ) {
      dispatch(setWarning({ upgrade: true }));
      dispatch(setWarning({ renew: false }));
    } else {
      dispatch(clearWarning());
    }
  }, [restaurantData, dispatch]);

  const handleClose = () => {
    dispatch(clearWarning());
  };

  if (!warning.show) return null;

  if (!renew && !upgrade && !noPlan) return null;

  return (
    <div
      className={`col-end-13 row-start-2 row-end-3 mb-5 flex items-center justify-between gap-5 rounded border px-4 py-5 md:px-6 ${bgColor}`}
      role="alert"
      aria-live="polite"
    >
      <p className="text-sm font-semibold">
        {renew && (
          <div className="flex flex-wrap items-center gap-1 text-[12px] md:text-sm">
            <RiErrorWarningFill size={18} />
            <span className="font-bold">
              {t("warningMessage.daysLeft", { count: daysLeft })} 
            </span>
            <span className="flex items-center gap-1">
              {t("warningMessage.upgrade1")}
            </span>
            <span>{t("warningMessage.renew")}</span>
            <button
              onClick={() => navigateToSubscribtion(navigate, token)}
              className="underline"
            >
              {t("warningMessage.PlanSettings")}
            </button>
          </div>
        )}

        {upgrade && (
          <div className="flex flex-wrap items-center gap-2 text-[12px] md:text-sm">
            <span className="flex items-center gap-1">
              <RiErrorWarningFill size={18} />
              {t("warningMessage.upgrade1")}
            </span>
            <span className="font-bold">
              {t("warningMessage.daysLeft", { count: daysLeft })} .
            </span>
            <span>{t("warningMessage.upgrade2")}</span>
            <button
              onClick={() => navigateToSubscribtion(navigate, token)}
              className="underline"
            >
              {t("warningMessage.PlanSettings")}
            </button>
          </div>
        )}

        {noPlan && (
          <div className="flex w-full flex-wrap items-center gap-2 text-[12px] md:text-sm">
            <span className="flex items-center gap-1">
              <RiErrorWarningFill size={18} />
              {t("warningMessage.noPlan")}
            </span>
          </div>
        )}
      </p>

      {noPlan ? (
        <button
          onClick={() => navigateToSubscribtion(navigate, token)}
          className="whitespace-nowrap text-[12px] font-semibold bg-red-300 border-2 border-red-400 p-2 rounded-md md:text-sm"
        >
          {t("warningMessage.PlanSettings")}
        </button>
      ) : (
        <button
          onClick={handleClose}
          className={`ml-4 text-lg transition-colors duration-150 ${hoverTextColor}`}
          aria-label="Close subscription warning"
        >
          <CgClose size={20} />
        </button>
      )}
    </div>
  );
}
